import type { QuotaNoticeDto } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCompanyZone, useMe } from '../../api/hooks.js';
import { useOrganizationAdmin } from '../../api/plan.js';
import { formatTokens } from '@dbrain/app-core/format';

/**
 * A run stopped because a token budget ran out (docs/design/mockups/KotaSohbet.dc.html,
 * D-97): which budget, when it resets, today's (or this month's) use, and
 * that the work done so far stays. "Yeniden dene" asks the server again: a
 * raised budget lets the work continue where it stopped; a budget that is
 * still used up shows its reset time again. The organization's admins get a
 * link to "Limitler ve kotalar" (Yönetim, the member's own row: D-100).
 */
export function BudgetNotice({
  quota,
  still,
  profile,
  errorCode,
  onRetry,
  retrying,
}: {
  /** what the stopped turn said; older turns may not carry it */
  quota: QuotaNoticeDto | null;
  /** a retry that found the budget still used up: its fresh reset time and use */
  still: QuotaNoticeDto | null;
  profile: 'chat' | 'code';
  errorCode: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const me = useMe().data;
  const zone = useCompanyZone();
  const admin = useOrganizationAdmin();
  const shown = still ?? quota;
  // on the company's clock: its 00:00 (D-119)
  const when = shown ? new Date(shown.resetsAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone }) : null;
  const use =
    shown?.used !== undefined && shown.limit !== undefined
      ? t(shown.scope === 'user_daily' ? 'quotaNotice.usedToday' : 'quotaNotice.usedMonth', {
          used: formatTokens(shown.used, i18n.language),
          limit: formatTokens(shown.limit, i18n.language),
        })
      : null;
  return (
    <section className="budget-notice" role="alert" aria-labelledby={titleId} data-testid="budget-notice" data-scope={shown?.scope ?? ''}>
      <span className="budget-notice__icon" aria-hidden="true">
        <Icons.time size={18} />
      </span>
      <div className="budget-notice__body">
        <h2 id={titleId} className="budget-notice__title">
          {shown ? t(`quotaNotice.${shown.scope}`) : t('errors.quota_exceeded.title')}
        </h2>
        <p className="budget-notice__text">
          {when ? (
            <>
              <span data-testid="budget-resets">
                <Trans i18nKey="quotaNotice.resets" values={{ when }} components={{ b: <strong /> }} />
              </span>{' '}
            </>
          ) : null}
          {t(`quotaNotice.kept.${profile}`)}
          {shown?.scope === 'tenant_monthly' && !admin ? ` ${t('quotaNotice.adminCanRaise')}` : ''}
        </p>
        {still ? (
          <p className="budget-notice__still" role="status" data-testid="budget-still">
            {t('quotaNotice.still')}
          </p>
        ) : null}
        {use ? (
          <p className="budget-notice__use" data-testid="budget-use">
            {use}
          </p>
        ) : null}
        <div className="budget-notice__actions">
          {admin ? (
            <Link className="ui-button ui-button--secondary ui-button--sm" to={`/admin/usage/members?q=${encodeURIComponent(me?.user.email ?? '')}`} data-testid="budget-limits">
              <Icons.usage size={15} />
              {t('quotaNotice.limits')}
            </Link>
          ) : null}
          {onRetry ? (
            <Button size="sm" icon={<Icons.regenerate size={15} />} busy={retrying} onClick={onRetry} data-testid="budget-retry">
              {t('common.retry')}
            </Button>
          ) : null}
          <details className="budget-notice__details">
            <summary>{t('common.technicalDetails')}</summary>
            <p>
              {t('common.errorCode')}: {errorCode}
              {shown ? ` · ${shown.scope}` : ''}
            </p>
          </details>
        </div>
      </div>
    </section>
  );
}
